import { useEffect, useState, useCallback } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { toast } from "sonner";
import { Printer, Plus, Trash2, Check, X, Users, ClipboardList, Trophy, MapPin, Image, Shield, LayoutGrid, CreditCard, Download, Bell } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail) || "Error");

export default function Admin() {
  usePageTitle("Control Panel - Lansdowne Youth Rugby", "Lansdowne Youth Rugby admin control panel.");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ regs: [], teams: [], programs: [], events: [], locations: [], gallery: [], users: [], rosters: null });

  const load = useCallback(async () => {
    try {
      const [regs, teams, programs, events, locations, gallery] = await Promise.all([
        api.get("/admin/registrations"), api.get("/teams"), api.get("/programs?all=true"),
        api.get("/events"), api.get("/locations"), api.get("/gallery"),
      ]);
      const extra = {};
      if (isAdmin) {
        const u = await api.get("/admin/users");
        extra.users = u.data;
      }
      setData((d) => ({ ...d, regs: regs.data, teams: teams.data, programs: programs.data, events: events.data, locations: locations.data, gallery: gallery.data, ...extra }));
    } catch (e) { err(e); }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const stats = {
    total: data.regs.length,
    approved: data.regs.filter((r) => r.approved).length,
    pending: data.regs.filter((r) => !r.approved).length,
    revenue: data.regs.filter((r) => r.payment_status === "paid").reduce((s, r) => s + r.amount_cents, 0),
  };

  return (
    <div className="bg-brand-black min-h-screen text-white">
      <div className="no-print"><Navbar /></div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-8 no-print">
          <div>
            <h1 className="font-display text-5xl tracking-wide">CONTROL PANEL</h1>
            <p className="text-white/50">Signed in as {user?.name} · <span className="text-brand-gold uppercase">{user?.role}</span></p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 no-print">
          <Stat label="Registrations" value={stats.total} />
          <Stat label="Approved" value={stats.approved} />
          <Stat label="Pending" value={stats.pending} />
          <Stat label="Revenue (paid)" value={`$${(stats.revenue / 100).toFixed(2)}`} />
        </div>

        <Tabs defaultValue="registrations">
          <TabsList className="bg-transparent gap-2 flex-wrap h-auto justify-start p-0 mb-8 no-print">
            <Tab value="registrations" icon={ClipboardList}>Registrations</Tab>
            <Tab value="rosters" icon={LayoutGrid}>Rosters</Tab>
            {isAdmin && <Tab value="teams" icon={Users}>Teams</Tab>}
            {isAdmin && <Tab value="events" icon={Trophy}>Events</Tab>}
            {isAdmin && <Tab value="programs" icon={ClipboardList}>Programs</Tab>}
            {isAdmin && <Tab value="locations" icon={MapPin}>Locations</Tab>}
            {isAdmin && <Tab value="gallery" icon={Image}>Gallery</Tab>}
            {isAdmin && <Tab value="moderators" icon={Shield}>Moderators</Tab>}
            {isAdmin && <Tab value="payments" icon={CreditCard}>Payments</Tab>}
            {isAdmin && <Tab value="notifications" icon={Bell}>Notifications</Tab>}
          </TabsList>

          <TabsContent value="registrations"><Registrations data={data} isAdmin={isAdmin} reload={load} /></TabsContent>
          <TabsContent value="rosters"><Rosters teams={data.teams} programs={data.programs} /></TabsContent>
          {isAdmin && <TabsContent value="teams"><Teams data={data} reload={load} /></TabsContent>}
          {isAdmin && <TabsContent value="events"><Events data={data} reload={load} /></TabsContent>}
          {isAdmin && <TabsContent value="programs"><Programs data={data} reload={load} /></TabsContent>}
          {isAdmin && <TabsContent value="locations"><Locations data={data} reload={load} /></TabsContent>}
          {isAdmin && <TabsContent value="gallery"><Gallery data={data} reload={load} /></TabsContent>}
          {isAdmin && <TabsContent value="moderators"><Moderators data={data} reload={load} me={user} /></TabsContent>}
          {isAdmin && <TabsContent value="payments"><Payments /></TabsContent>}
          {isAdmin && <TabsContent value="notifications"><Notifications /></TabsContent>}
        </Tabs>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="border border-white/10 bg-white/[0.03] p-5">
    <div className="text-xs uppercase tracking-wider text-white/50">{label}</div>
    <div className="font-display text-4xl text-brand-gold mt-1">{value}</div>
  </div>
);
const Tab = ({ value, icon: Icon, children }) => (
  <TabsTrigger value={value} className="rounded-none border border-white/15 data-[state=active]:bg-brand-red data-[state=active]:border-brand-red text-white/70 data-[state=active]:text-white px-4 py-2 font-bold uppercase tracking-wider text-xs">
    <Icon className="h-4 w-4 mr-2" />{children}
  </TabsTrigger>
);
const Panel = ({ children }) => <div className="border border-white/10 bg-white/[0.02] p-6">{children}</div>;
const inp = "bg-brand-black border-white/15 rounded-none text-white";

// ---------- Registrations ----------
function Registrations({ data, isAdmin, reload }) {
  const [assign, setAssign] = useState(null);
  const [teamSel, setTeamSel] = useState([]);
  const [expProgram, setExpProgram] = useState("");
  const [expStatus, setExpStatus] = useState("all");
  const progName = (id) => data.programs.find((p) => p.id === id)?.name || id;

  const approve = async (r, val) => { try { await api.put(`/admin/registrations/${r.id}/approve?approved=${val}`); reload(); toast.success(val ? "Approved" : "Unapproved"); } catch (e) { err(e); } };
  const del = async (r) => { if (!window.confirm("Delete this registration?")) return; try { await api.delete(`/admin/registrations/${r.id}`); reload(); } catch (e) { err(e); } };
  const saveTeams = async () => { try { await api.put(`/admin/registrations/${assign.id}/teams`, { team_ids: teamSel }); setAssign(null); reload(); toast.success("Teams assigned"); } catch (e) { err(e); } };

  const downloadExport = async (fmt) => {
    try {
      const params = new URLSearchParams();
      if (expProgram) params.set("program_id", expProgram);
      if (expStatus && expStatus !== "all") params.set("status", expStatus);
      const qs = params.toString();
      const res = await api.get(`/admin/registrations/export.${fmt}${qs ? `?${qs}` : ""}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `registrations.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) {
      let msg = "Export failed";
      try {
        const text = e?.response?.data ? await e.response.data.text() : null;
        if (text) { try { msg = JSON.parse(text).detail || msg; } catch { msg = text.slice(0, 200); } }
      } catch { /* ignore */ }
      toast.error(msg);
    }
  };

  return (
    <Panel>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="font-display text-2xl tracking-wide">Registrations</h3>
        <div className="flex gap-2 flex-wrap items-center">
          <select value={expProgram} onChange={(e) => setExpProgram(e.target.value)} className="bg-brand-black border border-white/15 text-white text-sm h-9 px-2 rounded-none" data-testid="export-program-filter">
            <option value="">All programs</option>
            {data.programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={expStatus} onChange={(e) => setExpStatus(e.target.value)} className="bg-brand-black border border-white/15 text-white text-sm h-9 px-2 rounded-none" data-testid="export-status-filter">
            <option value="all">All statuses</option>
            <option value="paid">Paid</option>
            <option value="cash_due">Cash due</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved only</option>
          </select>
          <Button size="sm" variant="outline" className="rounded-none border-white/20 bg-transparent text-white" onClick={() => downloadExport("csv")} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-1.5" /> CSV
          </Button>
          <Button size="sm" variant="outline" className="rounded-none border-white/20 bg-transparent text-white" onClick={() => downloadExport("xlsx")} data-testid="export-xlsx-btn">
            <Download className="h-4 w-4 mr-1.5" /> Excel
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/60">Player</TableHead>
              <TableHead className="text-white/60">Guardian</TableHead>
              <TableHead className="text-white/60">Programs</TableHead>
              <TableHead className="text-white/60">Payment</TableHead>
              <TableHead className="text-white/60">Teams</TableHead>
              <TableHead className="text-white/60">Status</TableHead>
              {isAdmin && <TableHead className="text-white/60 text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.regs.map((r) => (
              <TableRow key={r.id} className="border-white/10" data-testid={`reg-row-${r.id}`}>
                <TableCell>
                  <div className="font-semibold">{r.player_name}</div>
                  <div className="text-xs text-white/40">{r.gender} · DOB {r.dob}</div>
                  {r.allergies && <div className="text-xs text-brand-gold">⚠ {r.allergies}</div>}
                </TableCell>
                <TableCell className="text-sm">{r.guardian_name}<div className="text-xs text-white/40">{r.guardian_email}<br />{r.guardian_phone}</div></TableCell>
                <TableCell className="text-sm">{r.program_ids.map(progName).join(", ")}</TableCell>
                <TableCell className="text-sm">{money(r.amount_cents)}<div className="text-xs text-white/40">{r.payment_method} · {r.payment_status}</div></TableCell>
                <TableCell className="text-xs">{(r.team_ids || []).map((t) => data.teams.find((x) => x.id === t)?.name).filter(Boolean).join(", ") || "—"}</TableCell>
                <TableCell>{r.approved ? <Badge className="bg-green-600 rounded-none">Approved</Badge> : <Badge className="bg-white/15 rounded-none">Pending</Badge>}</TableCell>
                {isAdmin && (
                  <TableCell className="text-right whitespace-nowrap">
                    {!r.approved
                      ? <Button size="sm" className="bg-green-600 hover:bg-green-700 rounded-none mr-1" onClick={() => approve(r, true)} data-testid={`approve-${r.id}`}><Check className="h-4 w-4" /></Button>
                      : <Button size="sm" variant="outline" className="rounded-none mr-1 border-white/20 bg-transparent" onClick={() => approve(r, false)}><X className="h-4 w-4" /></Button>}
                    <Button size="sm" variant="outline" className="rounded-none mr-1 border-white/20 bg-transparent text-white" onClick={() => { setAssign(r); setTeamSel(r.team_ids || []); }} data-testid={`assign-${r.id}`}><Users className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" className="rounded-none border-red-500/40 bg-transparent text-red-400" onClick={() => del(r)}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {data.regs.length === 0 && <TableRow><TableCell colSpan={7} className="text-white/40 text-center py-8">No registrations yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!assign} onOpenChange={(o) => !o && setAssign(null)}>
        <DialogContent className="bg-brand-black border-white/15 text-white rounded-none">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-wide">Assign {assign?.player_name} to Teams</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {data.teams.map((t) => (
              <label key={t.id} className="flex items-center gap-3 p-3 border border-white/10 cursor-pointer">
                <Checkbox checked={teamSel.includes(t.id)} onCheckedChange={(v) => setTeamSel((s) => v ? [...s, t.id] : s.filter((x) => x !== t.id))} className="border-white/40 data-[state=checked]:bg-brand-red" />
                <span>{t.name} <span className="text-white/40 text-sm">{t.age_group}</span></span>
              </label>
            ))}
            {data.teams.length === 0 && <p className="text-white/40">Create teams first.</p>}
          </div>
          <Button className="bg-brand-red rounded-none font-bold uppercase" onClick={saveTeams} data-testid="save-teams">Save Assignment</Button>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

// ---------- Rosters ----------
function Rosters() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/rosters").then((r) => setData(r.data)).catch(err); }, []);
  const downloadPdf = async () => {
    try {
      const res = await api.get("/admin/rosters/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "rosters.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Roster PDF downloaded");
    } catch (e) { err(e); }
  };
  if (!data) return <Panel><p className="text-white/40">Loading…</p></Panel>;
  return (
    <div>
      <div className="flex justify-end gap-2 mb-4 no-print">
        <Button variant="outline" className="rounded-none border-white/20 bg-transparent text-white" onClick={downloadPdf} data-testid="download-roster-pdf"><Download className="h-4 w-4 mr-2" /> Download PDF</Button>
        <Button className="bg-brand-gold text-brand-black rounded-none font-bold uppercase tracking-wider" onClick={() => window.print()} data-testid="print-rosters"><Printer className="h-4 w-4 mr-2" /> Print Rosters</Button>
      </div>
      <div className="print-area space-y-6">
        {data.teams.map(({ team, players }) => (
          <div key={team.id} className="border border-white/10 bg-white/[0.02] p-6" data-testid={`roster-${team.id}`}>
            <h3 className="font-display text-3xl tracking-wide text-brand-gold mb-1">{team.name}</h3>
            <p className="text-white/40 text-sm mb-4">{team.age_group} · {players.length} players</p>
            <Table>
              <TableHeader><TableRow className="border-white/10 hover:bg-transparent"><TableHead className="text-white/60">#</TableHead><TableHead className="text-white/60">Player</TableHead><TableHead className="text-white/60">Gender</TableHead><TableHead className="text-white/60">DOB</TableHead><TableHead className="text-white/60">Guardian</TableHead><TableHead className="text-white/60">Phone</TableHead><TableHead className="text-white/60">Allergies</TableHead></TableRow></TableHeader>
              <TableBody>
                {players.map((p, i) => (
                  <TableRow key={p.id} className="border-white/10"><TableCell>{i + 1}</TableCell><TableCell className="font-semibold">{p.player_name}</TableCell><TableCell>{p.gender}</TableCell><TableCell>{p.dob}</TableCell><TableCell>{p.guardian_name}</TableCell><TableCell>{p.guardian_phone}</TableCell><TableCell>{p.allergies || "—"}</TableCell></TableRow>
                ))}
                {players.length === 0 && <TableRow><TableCell colSpan={7} className="text-white/40 text-center py-4">No players assigned.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        ))}
        {data.unassigned?.length > 0 && (
          <div className="border border-white/10 bg-white/[0.02] p-6">
            <h3 className="font-display text-2xl tracking-wide text-white/70 mb-3">Unassigned (approved)</h3>
            {data.unassigned.map((p) => <div key={p.id} className="text-sm py-1 border-b border-white/5">{p.player_name} · {p.gender} · {p.guardian_phone}</div>)}
          </div>
        )}
        {data.teams.length === 0 && <Panel><p className="text-white/40">No teams yet.</p></Panel>}
      </div>
    </div>
  );
}

// ---------- Teams ----------
function Teams({ data, reload }) {
  const [f, setF] = useState({ name: "", age_group: "" });
  const add = async () => { if (!f.name) return; try { await api.post("/admin/teams", f); setF({ name: "", age_group: "" }); reload(); toast.success("Team created"); } catch (e) { err(e); } };
  const del = async (id) => { if (!window.confirm("Delete team?")) return; try { await api.delete(`/admin/teams/${id}`); reload(); } catch (e) { err(e); } };
  return (
    <Panel>
      <div className="flex flex-wrap gap-3 mb-6">
        <Input placeholder="Team name (e.g. U10 Reds)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${inp} max-w-xs`} data-testid="team-name" />
        <Input placeholder="Age group (e.g. Under 10)" value={f.age_group} onChange={(e) => setF({ ...f, age_group: e.target.value })} className={`${inp} max-w-xs`} data-testid="team-age" />
        <Button className="bg-brand-red rounded-none font-bold uppercase" onClick={add} data-testid="add-team"><Plus className="h-4 w-4 mr-1" /> Add Team</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.teams.map((t) => (
          <div key={t.id} className="border border-white/10 p-5 flex justify-between items-start">
            <div><div className="font-display text-2xl tracking-wide">{t.name}</div><div className="text-white/40 text-sm">{t.age_group}</div></div>
            <Button size="sm" variant="outline" className="rounded-none border-red-500/40 bg-transparent text-red-400" onClick={() => del(t.id)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------- Programs ----------
function Programs({ data, reload }) {
  const [f, setF] = useState({ name: "", description: "", price: "", image_url: "", start_date: "", capacity: "", reminder_days: "" });
  const add = async () => { if (!f.name) return; try { await api.post("/admin/programs", { name: f.name, description: f.description, price_cents: Math.round(parseFloat(f.price || "0") * 100), image_url: f.image_url, start_date: f.start_date, capacity: parseInt(f.capacity || "0", 10), reminder_days: parseInt(f.reminder_days || "0", 10), active: true }); setF({ name: "", description: "", price: "", image_url: "", start_date: "", capacity: "", reminder_days: "" }); reload(); toast.success("Program added"); } catch (e) { err(e); } };
  const del = async (id) => { if (!window.confirm("Delete program?")) return; try { await api.delete(`/admin/programs/${id}`); reload(); } catch (e) { err(e); } };
  const upd = async (p, changes) => { try { await api.put(`/admin/programs/${p.id}`, { name: p.name, description: p.description, price_cents: p.price_cents, image_url: p.image_url, active: p.active, start_date: p.start_date || "", capacity: p.capacity || 0, reminder_days: p.reminder_days || 0, ...changes }); reload(); } catch (e) { err(e); } };
  return (
    <Panel>
      <div className="grid sm:grid-cols-2 gap-3 mb-3 max-w-3xl">
        <Input placeholder="Program name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="program-name" />
        <Input placeholder="Price (e.g. 75)" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={inp} data-testid="program-price" />
        <div>
          <label className="text-white/60 text-xs uppercase tracking-wider block mb-1">Start Date (for reminders)</label>
          <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={inp} data-testid="program-start-date" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-white/60 text-xs uppercase tracking-wider block mb-1">Capacity (0 = ∞)</label>
            <Input type="number" min="0" placeholder="0" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} className={inp} data-testid="program-capacity" />
          </div>
          <div>
            <label className="text-white/60 text-xs uppercase tracking-wider block mb-1">Remind days (0 = default)</label>
            <Input type="number" min="0" placeholder="0" value={f.reminder_days} onChange={(e) => setF({ ...f, reminder_days: e.target.value })} className={inp} data-testid="program-reminder-days" />
          </div>
        </div>
        <Input placeholder="Image URL" value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} className={inp} />
        <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={`${inp} sm:col-span-2`} />
      </div>
      <Button className="bg-brand-red rounded-none font-bold uppercase mb-6" onClick={add} data-testid="add-program"><Plus className="h-4 w-4 mr-1" /> Add Program</Button>
      <div className="space-y-3">
        {data.programs.map((p) => (
          <div key={p.id} className="border border-white/10 p-4 flex flex-wrap justify-between items-center gap-3">
            <div>
              <span className="font-display text-2xl tracking-wide mr-3">{p.name}</span><span className="text-brand-gold">{money(p.price_cents)}</span>
              {!p.active && <Badge className="ml-2 bg-white/15 rounded-none">Hidden</Badge>}
              <span className="text-white/40 text-xs ml-3">{p.capacity ? `${p.registered_count || 0}/${p.capacity} spots` : `${p.registered_count || 0} signed up`}</span>
              {p.is_full && <Badge className="ml-2 bg-brand-red rounded-none uppercase text-[10px]">Full</Badge>}
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <div className="flex items-center gap-1.5">
                <span className="text-white/50 text-xs">Starts</span>
                <Input type="date" value={p.start_date || ""} onChange={(e) => upd(p, { start_date: e.target.value })} className={`${inp} h-9 w-40`} data-testid={`program-date-${p.id}`} />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-white/50 text-xs">Cap</span>
                <Input type="number" min="0" defaultValue={p.capacity || 0} onBlur={(e) => upd(p, { capacity: parseInt(e.target.value || "0", 10) })} className={`${inp} h-9 w-16`} data-testid={`program-cap-${p.id}`} />
              </div>
              <Button size="sm" variant="outline" className="rounded-none border-white/20 bg-transparent text-white" onClick={() => upd(p, { active: !p.active })}>{p.active ? "Hide" : "Show"}</Button>
              <Button size="sm" variant="outline" className="rounded-none border-red-500/40 bg-transparent text-red-400" onClick={() => del(p.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------- Locations ----------
function Locations({ data, reload }) {
  const [f, setF] = useState({ name: "", address: "", thumbnail: "" });
  const add = async () => { if (!f.name) return; try { await api.post("/admin/locations", f); setF({ name: "", address: "", thumbnail: "" }); reload(); toast.success("Location saved"); } catch (e) { err(e); } };
  const del = async (id) => { if (!window.confirm("Delete location?")) return; try { await api.delete(`/admin/locations/${id}`); reload(); } catch (e) { err(e); } };
  return (
    <Panel>
      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        <Input placeholder="Location name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="loc-name" />
        <Input placeholder="Address (used for directions)" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={inp} data-testid="loc-address" />
        <Input placeholder="Thumbnail URL" value={f.thumbnail} onChange={(e) => setF({ ...f, thumbnail: e.target.value })} className={inp} />
      </div>
      <Button className="bg-brand-red rounded-none font-bold uppercase mb-6" onClick={add} data-testid="add-location"><Plus className="h-4 w-4 mr-1" /> Save Location</Button>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.locations.map((l) => (
          <div key={l.id} className="border border-white/10 overflow-hidden">
            {l.thumbnail && <img src={l.thumbnail} alt={l.name} className="h-28 w-full object-cover" />}
            <div className="p-4">
              <div className="font-display text-xl tracking-wide">{l.name}</div>
              <div className="text-white/40 text-sm mb-3">{l.address}</div>
              <div className="flex gap-2">
                {l.maps_url && <a href={l.maps_url} target="_blank" rel="noreferrer" className="flex-1"><Button size="sm" className="w-full bg-transparent border border-brand-gold text-brand-gold rounded-none text-xs">Directions</Button></a>}
                <Button size="sm" variant="outline" className="rounded-none border-red-500/40 bg-transparent text-red-400" onClick={() => del(l.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------- Events ----------
function Events({ data, reload }) {
  const [f, setF] = useState({ title: "", category: "training", description: "", location_id: "", start_time: "", thumbnail: "" });
  const add = async () => { if (!f.title) return; try { await api.post("/admin/events", { ...f, location_id: f.location_id || null }); setF({ title: "", category: "training", description: "", location_id: "", start_time: "", thumbnail: "" }); reload(); toast.success("Event created"); } catch (e) { err(e); } };
  const del = async (id) => { if (!window.confirm("Delete event?")) return; try { await api.delete(`/admin/events/${id}`); reload(); } catch (e) { err(e); } };
  return (
    <Panel>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <Input placeholder="Event title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} data-testid="event-title" />
        <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
          <SelectTrigger className={inp} data-testid="event-category"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-brand-black border-white/15 text-white rounded-none">
            <SelectItem value="training">Training</SelectItem><SelectItem value="tournament">Tournament / Blitz</SelectItem><SelectItem value="special">Special Event</SelectItem>
          </SelectContent>
        </Select>
        <Select value={f.location_id} onValueChange={(v) => setF({ ...f, location_id: v })}>
          <SelectTrigger className={inp} data-testid="event-location"><SelectValue placeholder="Location" /></SelectTrigger>
          <SelectContent className="bg-brand-black border-white/15 text-white rounded-none">
            {data.locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="datetime-local" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className={inp} data-testid="event-time" />
        <Input placeholder="Thumbnail URL" value={f.thumbnail} onChange={(e) => setF({ ...f, thumbnail: e.target.value })} className={`${inp} sm:col-span-2`} />
        <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={`${inp} sm:col-span-2`} />
      </div>
      <Button className="bg-brand-red rounded-none font-bold uppercase mb-6" onClick={add} data-testid="add-event"><Plus className="h-4 w-4 mr-1" /> Add Event</Button>
      <div className="space-y-3">
        {data.events.map((e) => (
          <div key={e.id} className="border border-white/10 p-4 flex justify-between items-center">
            <div><Badge className="bg-brand-red rounded-none mr-3 uppercase text-[10px]">{e.category}</Badge><span className="font-display text-xl tracking-wide">{e.title}</span><span className="text-white/40 text-sm ml-3">{e.location?.name}</span></div>
            <Button size="sm" variant="outline" className="rounded-none border-red-500/40 bg-transparent text-red-400" onClick={() => del(e.id)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------- Gallery ----------
function Gallery({ data, reload }) {
  const [f, setF] = useState({ image_url: "", caption: "", event_id: "" });
  const add = async () => { if (!f.image_url) return; try { await api.post("/admin/gallery", { image_url: f.image_url, caption: f.caption, event_id: f.event_id || null }); setF({ image_url: "", caption: "", event_id: "" }); reload(); toast.success("Photo added"); } catch (e) { err(e); } };
  const del = async (id) => { try { await api.delete(`/admin/gallery/${id}`); reload(); } catch (e) { err(e); } };
  return (
    <Panel>
      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        <Input placeholder="Image URL" value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} className={inp} data-testid="gallery-url" />
        <Input placeholder="Caption (optional)" value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} className={inp} />
        <Select value={f.event_id} onValueChange={(v) => setF({ ...f, event_id: v })}>
          <SelectTrigger className={inp}><SelectValue placeholder="Link to event (optional)" /></SelectTrigger>
          <SelectContent className="bg-brand-black border-white/15 text-white rounded-none">
            {data.events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button className="bg-brand-red rounded-none font-bold uppercase mb-6" onClick={add} data-testid="add-gallery"><Plus className="h-4 w-4 mr-1" /> Add Photo</Button>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
        {data.gallery.map((g) => (
          <div key={g.id} className="relative group border border-white/10">
            <img src={g.image_url} alt={g.caption} className="w-full h-28 object-cover" />
            <button onClick={() => del(g.id)} className="absolute top-1 right-1 bg-red-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------- Moderators ----------
function Moderators({ data, reload, me }) {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const addCoach = async () => { if (!f.email || !f.password) return; try { await api.post("/admin/coaches", f); setF({ name: "", email: "", password: "" }); reload(); toast.success("Coach added"); } catch (e) { err(e); } };
  const setRole = async (u, role) => { try { await api.put(`/admin/users/${u.id}/role`, { role }); reload(); toast.success("Role updated"); } catch (e) { err(e); } };
  return (
    <Panel>
      <h3 className="font-display text-2xl tracking-wide mb-4">Add Coach / Moderator</h3>
      <div className="grid sm:grid-cols-3 gap-3 mb-3 max-w-3xl">
        <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="coach-name" />
        <Input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} data-testid="coach-email" />
        <Input type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inp} data-testid="coach-password" />
      </div>
      <Button className="bg-brand-red rounded-none font-bold uppercase mb-8" onClick={addCoach} data-testid="add-coach"><Plus className="h-4 w-4 mr-1" /> Add Coach</Button>
      <div className="space-y-2">
        {(data.users || []).map((u) => (
          <div key={u.id} className="border border-white/10 p-4 flex justify-between items-center">
            <div><span className="font-semibold">{u.name || u.email}</span> <span className="text-white/40 text-sm ml-2">{u.email}</span></div>
            <div className="flex items-center gap-3">
              <Badge className={`rounded-none uppercase ${u.role === "admin" ? "bg-brand-red" : u.role === "coach" ? "bg-brand-gold text-brand-black" : "bg-white/15"}`}>{u.role}</Badge>
              {u.id !== me.id && (
                <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                  <SelectTrigger className={`${inp} w-32 h-9`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-brand-black border-white/15 text-white rounded-none">
                    <SelectItem value="user">User</SelectItem><SelectItem value="coach">Coach</SelectItem><SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------- Payments (Authorize.Net) ----------
function Payments() {
  const [s, setS] = useState(null);
  const [tk, setTk] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/payment-settings").then((r) => { setS(r.data); setTk(""); }).catch(err);
  useEffect(() => { load(); }, []);
  if (!s) return <Panel><p className="text-white/40">Loading…</p></Panel>;

  const upd = (patch) => setS((v) => ({ ...v, ...patch }));
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        api_login_id: s.api_login_id || "",
        public_client_key: s.public_client_key || "",
        environment: s.environment,
        cash_on_field_enabled: s.cash_on_field_enabled,
      };
      if (tk) body.transaction_key = tk;
      const { data } = await api.put("/admin/payment-settings", body);
      toast.success(data.payment_configured ? "Saved — card payments are LIVE" : "Saved");
      load();
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  return (
    <Panel>
      <div className="flex items-center gap-3 mb-2">
        <h3 className="font-display text-2xl tracking-wide">Authorize.Net</h3>
        {s.payment_configured
          ? <Badge className="bg-green-600 rounded-none uppercase" data-testid="pay-status">Configured</Badge>
          : <Badge className="bg-white/15 rounded-none uppercase" data-testid="pay-status">Not configured</Badge>}
      </div>
      <p className="text-white/50 text-sm mb-6 max-w-2xl">Enter your Authorize.Net credentials to accept card payments. Find these in your Authorize.Net Merchant portal under <span className="text-white/70">Account → Security Settings → API Credentials &amp; Keys</span>. The Transaction Key can only be viewed once when generated — if you lost it, generate a new one there and paste it below.</p>

      <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
        <div>
          <Label className="text-white/80 mb-1.5 block">API Login ID</Label>
          <Input value={s.api_login_id || ""} onChange={(e) => upd({ api_login_id: e.target.value })} className={inp} data-testid="pay-api-login" />
        </div>
        <div>
          <Label className="text-white/80 mb-1.5 block">Transaction Key</Label>
          <Input type="password" value={tk} onChange={(e) => setTk(e.target.value)} placeholder={s.has_transaction_key ? "•••••••• (leave blank to keep)" : "Enter transaction key"} className={inp} data-testid="pay-transaction-key" />
        </div>
        <div>
          <Label className="text-white/80 mb-1.5 block">Public Client Key</Label>
          <Input value={s.public_client_key || ""} onChange={(e) => upd({ public_client_key: e.target.value })} className={inp} data-testid="pay-public-key" />
        </div>
        <div>
          <Label className="text-white/80 mb-1.5 block">Environment</Label>
          <Select value={s.environment} onValueChange={(v) => upd({ environment: v })}>
            <SelectTrigger className={inp} data-testid="pay-env"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-brand-black border-white/15 text-white rounded-none">
              <SelectItem value="sandbox">Sandbox (testing)</SelectItem>
              <SelectItem value="production">Production (live)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <label className="flex items-center gap-3 mt-6 cursor-pointer">
        <Checkbox checked={s.cash_on_field_enabled} onCheckedChange={(v) => upd({ cash_on_field_enabled: !!v })} className="border-white/40 data-[state=checked]:bg-brand-red data-[state=checked]:border-brand-red" data-testid="pay-cash-toggle" />
        <span>Allow "Cash on Field" as a payment option</span>
      </label>

      <Button className="bg-brand-red rounded-none font-bold uppercase mt-6" onClick={save} disabled={saving} data-testid="pay-save">
        {saving ? "Saving…" : "Save Payment Settings"}
      </Button>
    </Panel>
  );
}

// ---------- Notifications (signup email recipients) ----------
function Notifications() {
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reminderDays, setReminderDays] = useState(3);
  const [savingReminder, setSavingReminder] = useState(false);

  useEffect(() => {
    api.get("/admin/notification-settings")
      .then((r) => setRecipients(r.data.recipients || []))
      .catch(err)
      .finally(() => setLoading(false));
    api.get("/admin/reminder-settings")
      .then((r) => setReminderDays(r.data.days_before ?? 3))
      .catch(() => {});
  }, []);

  const saveReminder = async () => {
    setSavingReminder(true);
    try {
      const { data } = await api.put("/admin/reminder-settings", { days_before: parseInt(reminderDays || "0", 10) });
      setReminderDays(data.days_before);
      toast.success("Reminder timing saved");
    } catch (e) { err(e); } finally { setSavingReminder(false); }
  };

  const add = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    if (recipients.some((r) => r.email === email)) return toast.info("That email is already on the list");
    setRecipients((rs) => [...rs, { email, enabled: true }]);
    setNewEmail("");
  };
  const toggle = (i) => setRecipients((rs) => rs.map((r, idx) => (idx === i ? { ...r, enabled: !r.enabled } : r)));
  const remove = (i) => setRecipients((rs) => rs.filter((_, idx) => idx !== i));
  const allOn = recipients.length > 0 && recipients.every((r) => r.enabled);
  const toggleAll = () => setRecipients((rs) => rs.map((r) => ({ ...r, enabled: !allOn })));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/notification-settings", { recipients });
      setRecipients(data.recipients || []);
      toast.success("Notification recipients saved");
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  if (loading) return <Panel><p className="text-white/40">Loading…</p></Panel>;

  return (
    <Panel>
      <h3 className="font-display text-2xl tracking-wide mb-2">Signup Notifications</h3>
      <p className="text-white/50 text-sm mb-6 max-w-2xl">Add the email addresses that should receive a notification whenever a new registration comes in. Tick the ones you want active — only checked addresses receive emails.</p>

      <div className="flex gap-2 mb-5 max-w-lg">
        <Input
          type="email" placeholder="name@example.com" value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className={inp} data-testid="notify-new-email"
        />
        <Button className="bg-brand-red rounded-none font-bold uppercase" onClick={add} data-testid="notify-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      {recipients.length > 0 && (
        <label className="flex items-center gap-3 mb-3 cursor-pointer text-sm text-white/70">
          <Checkbox checked={allOn} onCheckedChange={toggleAll} className="border-white/40 data-[state=checked]:bg-brand-red data-[state=checked]:border-brand-red" data-testid="notify-select-all" />
          <span>Select all</span>
        </label>
      )}

      <div className="space-y-2 max-w-lg">
        {recipients.map((r, i) => (
          <div key={r.email} className="flex items-center justify-between gap-3 p-3 border border-white/10" data-testid={`notify-row-${i}`}>
            <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
              <Checkbox checked={r.enabled} onCheckedChange={() => toggle(i)} className="border-white/40 data-[state=checked]:bg-brand-red data-[state=checked]:border-brand-red" data-testid={`notify-toggle-${i}`} />
              <span className="truncate">{r.email}</span>
            </label>
            <Button size="sm" variant="outline" className="rounded-none border-red-500/40 bg-transparent text-red-400" onClick={() => remove(i)} data-testid={`notify-remove-${i}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {recipients.length === 0 && <p className="text-white/40">No recipients yet — new signups won't send notification emails.</p>}
      </div>

      <Button className="bg-brand-red rounded-none font-bold uppercase mt-6" onClick={save} disabled={saving} data-testid="notify-save-btn">
        {saving ? "Saving…" : "Save Recipients"}
      </Button>

      <div className="border-t border-white/10 mt-10 pt-8 max-w-lg">
        <h3 className="font-display text-2xl tracking-wide mb-2">Reminder Timing</h3>
        <p className="text-white/50 text-sm mb-4">Default number of days before a program's start date that guardians get a reminder email. Individual programs can override this in the Programs tab.</p>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-white/80 mb-1.5 block">Days before</Label>
            <Input type="number" min="0" max="60" value={reminderDays} onChange={(e) => setReminderDays(e.target.value)} className={`${inp} w-28`} data-testid="reminder-days-input" />
          </div>
          <Button className="bg-brand-red rounded-none font-bold uppercase" onClick={saveReminder} disabled={savingReminder} data-testid="reminder-save-btn">
            {savingReminder ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Panel>
  );
}


